import { useState } from 'react';
import { useEmail } from './useEmail';
import { apiClient } from '@/shared/services/apiClient';
import { API_ROUTES } from '@/shared/services/apiRoutes';
import { supabase } from '@/integrations/supabase/client';

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenResolution: string;
  timezone: string;
}

export const useDeviceTracking = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { sendEmail } = useEmail();

  const generateDeviceFingerprint = (): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint test', 2, 2);
    }
    
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvasFingerprint: canvas.toDataURL(),
    };
    
    return btoa(JSON.stringify(deviceInfo)).substring(0, 50);
  };

  const getDeviceInfo = (): DeviceInfo => ({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const checkAndNotifyLogin = async () => {
    setIsLoading(true);

    try {
      console.log('[DeviceTracking] Starting device tracking check...');

      // ✅ NEW: Use backend API for device tracking
      const deviceInfo = getDeviceInfo();
      const deviceFingerprint = generateDeviceFingerprint();

      // Detect device type
      const ua = navigator.userAgent.toLowerCase();
      const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
      const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(ua);

      // Parse browser and OS
      const browser = ua.includes('chrome') ? 'Chrome' :
                      ua.includes('safari') ? 'Safari' :
                      ua.includes('firefox') ? 'Firefox' :
                      ua.includes('edge') ? 'Edge' : 'Unknown';

      const os = ua.includes('windows') ? 'Windows' :
                 ua.includes('mac') ? 'MacOS' :
                 ua.includes('linux') ? 'Linux' :
                 ua.includes('android') ? 'Android' :
                 ua.includes('ios') ? 'iOS' : deviceInfo.platform;

      console.log('[DeviceTracking] Device info:', { browser, os, isMobile });

      const response = await apiClient.post<{ ok: boolean; data: any; updated: boolean }>(
        API_ROUTES.DEVICES_TRACK,
        {
          device_type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
          browser,
          os,
          device_model: deviceInfo.platform,
          screen_width: screen.width,
          screen_height: screen.height,
          user_agent: deviceInfo.userAgent,
          is_mobile: isMobile,
          is_tablet: isTablet,
          location: deviceInfo.timezone,
        }
      );

      console.log('[DeviceTracking] Device tracked:', { updated: response.updated });

      // If this was a new device (not updated), send notification email
      if (!response.updated) {
        try {
          // Get user email from Supabase Auth
          const { data: { user } } = await supabase.auth.getUser();
          const userEmail = user?.email;

          console.log('[DeviceTracking] New device detected, sending email to:', userEmail);

          if (userEmail) {
            await sendEmail({
              to: userEmail,
              subject: 'New Device Login Detected',
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #333;">New Device Login Detected</h2>
                  <p>Hi there,</p>
                  <p>We detected a login to your account from a new device. If this was you, you can safely ignore this email.</p>

                  <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
                    <h3 style="margin-top: 0; color: #555;">Login Details:</h3>
                    <p><strong>Time:</strong> ${new Date().toLocaleString()}</p>
                    <p><strong>Platform:</strong> ${os}</p>
                    <p><strong>Browser:</strong> ${browser}</p>
                    <p><strong>Location:</strong> ${deviceInfo.timezone}</p>
                  </div>

                  <p>If you did not log in from this device, please secure your account immediately by changing your password.</p>

                  <p>Best regards,<br>Your Security Team</p>
                </div>
              `,
            });
            console.log('✅ [DeviceTracking] Device detection email sent successfully');
          } else {
            console.warn('⚠️ [DeviceTracking] Could not send device notification - no user email found');
          }
        } catch (emailError) {
          console.error('❌ [DeviceTracking] Failed to send login notification email:', emailError);
        }
      }
    } catch (error) {
      console.error('❌ [DeviceTracking] Device tracking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserDevices = async () => {
    try {
      // ✅ NEW: Use backend API for fetching devices
      const response = await apiClient.get<{ ok: boolean; data: any[] }>(API_ROUTES.DEVICES_LIST);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching user devices:', error);
      return [];
    }
  };

  return {
    checkAndNotifyLogin,
    getUserDevices,
    isLoading,
  };
};